/**
 * Music Life 的搜尋、歌單與播放器介面，由 main.jsx 載入。
 * 只使用既有播放 API、回報真實播放事件；音源驗證與備援仍由 Worker 決定。
 * 共用 Modal 管理對話框，playbackNavigation 管理介面時間與播放順序。
 */
import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "./Modal.jsx";
import SettingsSheet from "./SettingsSheet.jsx";
import AddDestinationSheet from "./components/AddDestinationSheet.jsx";
import LyricsPicker from "./components/LyricsPicker.jsx";
import { Artwork, Icon, SongRow } from "./components/Media.jsx";
import { lyricChoiceKey, parseLyricRows } from "./lyrics.js";
import { providerLabel } from "./musicMetadata.js";
import {
  formatTime,
  nextPlaybackMode,
  normalizePlaybackMode,
  preparedPlaybackIsUsable,
  restoreSongAt,
  songForPlaybackMode,
} from "./playbackNavigation.js";
import {
  mergeRefreshedSong,
  playbackRefNeedsRefresh,
  selectPlaybackRefreshMatch,
  shouldRefreshPlaybackData,
} from "./playbackIdentity.js";
import {
  aggregateApiHealth,
  normalizeSourceSelection,
  selectionForSourceChoice,
} from "./settingsModel.js";
import { useApiHealth } from "./useApiHealth.js";
import { usePwaInstall } from "./usePwaInstall.js";

const API_BASE = String(
  import.meta.env.VITE_API_BASE_URL || "https://music-life.tn289723.workers.dev"
).replace(/\/$/, "");
const MARKET = "TW";
const SOURCE_OPTIONS = [
  { id: "musicsquare-qq", label: "QQ", detail: "主要 QQ 音源" },
  { id: "musicsquare-netease", label: "網易", detail: "主要跨平台音源" },
  { id: "musicsquare-kuwo", label: "酷我", detail: "主要跨平台音源" },
  { id: "yinyueku", label: "Yinyueku", detail: "第四音源・聚合補充" },
];
const SOURCE_IDS = SOURCE_OPTIONS.map((item) => item.id);
const PREVIOUS_AUTO_SOURCE_IDS = ["musicsquare-qq", "musicsquare-netease", "musicsquare-kuwo"];
const STORE = {
  favorites: "music-life:favorites:v1",
  playlists: "music-life:playlists:v1",
  queue: "music-life:queue:v1",
  lyricChoices: "music-life:lyric-choices:v2",
  playbackMode: "music-life:playback-mode:v1",
  sources: "music-life:sources:v1",
};
const PLAYBACK_MODE_META = {
  list: { label: "列表循環", icon: "repeat" },
  one: { label: "單曲循環", icon: "repeatOne" },
  shuffle: { label: "隨機播放", icon: "shuffle" },
};
const NAVIGATION = [
  ["search", "搜尋", "search"],
  ["favorites", "我的收藏", "heart"],
  ["playlists", "自建歌單", "music"],
];
const PAGE_TITLES = {
  search: "搜尋音樂",
  favorites: "我的收藏",
  playlists: "自建歌單",
  queue: "播放列表",
};
const SEARCH_SKELETON_ROWS = [0, 1, 2, 3];
const DEFAULT_PLAYLISTS = [{ id: "default", name: "我的歌單", songs: [] }];

function text(value) { return String(value ?? "").trim(); }
function readStore(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}
/**
 * 將讀取、狀態與寫入收斂成同一個 Hook。
 * 瀏覽器拒絕儲存時仍保留記憶體狀態，避免私人模式或容量不足中斷播放器。
 */
function useStoredState(key, fallback, normalize = (value) => value) {
  const [value, setValue] = useState(() => normalize(readStore(key, fallback)));
  useEffect(() => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* 保持播放器可用 */ }
  }, [key, value]);
  return [value, setValue];
}
function userMessage(payload, fallback) {
  return payload?.error?.userMessage || payload?.error?.message || fallback;
}
function candidateId(candidate) {
  return candidate?.candidateId || candidate?.sourceId || candidate?.embedId || candidate?.url || "";
}
function sessionCandidates(session) {
  return session ? [session.selected, ...(session.fallbackCandidates || [])].filter(Boolean) : [];
}
function streamUrl(session, candidate) {
  const correlation = session?.correlation || {};
  const params = new URLSearchParams({
    sessionId: session?.sessionId || "",
    correlationId: correlation.correlationId || session?.correlationId || "",
    decisionId: session?.decisionId || "",
    candidateId: candidate?.candidateId || candidate?.sourceId || "",
    sourceId: candidate?.sourceId || "",
    provider: candidate?.provider || "",
    playbackMode: candidate?.playbackMode || "",
    market: session?.market || MARKET,
  });
  return `${API_BASE}/api/v1/playback/stream?${params}`;
}

export default function App() {
  const [keyword, setKeyword] = useState("");
  const [songs, setSongs] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedSources, setSelectedSources] = useStoredState(
    STORE.sources,
    SOURCE_IDS,
    (value) => normalizeSourceSelection(value, SOURCE_IDS, PREVIOUS_AUTO_SOURCE_IDS)
  );
  const [resultLimit, setResultLimit] = useState(10);
  const [loadingRef, setLoadingRef] = useState("");
  const [activeTab, setActiveTab] = useState("search");
  const [playerOpen, setPlayerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [playlistEditor, setPlaylistEditor] = useState(null);
  const [deletePlaylistTarget, setDeletePlaylistTarget] = useState(null);
  const [pendingUndo, setPendingUndo] = useState(null);
  const [searchState, setSearchState] = useState("idle");
  const [searchedKeyword, setSearchedKeyword] = useState("");
  const [favorites, setFavorites] = useStoredState(STORE.favorites, []);
  const [playlists, setPlaylists] = useStoredState(STORE.playlists, DEFAULT_PLAYLISTS);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState("default");
  const [queue, setQueue] = useStoredState(STORE.queue, []);
  const [session, setSession] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [currentSong, setCurrentSong] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const [loadingAudio, setLoadingAudio] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackMode, setPlaybackMode] = useStoredState(
    STORE.playbackMode,
    "list",
    normalizePlaybackMode
  );
  const [showLyrics, setShowLyrics] = useState(false);
  const [lyrics, setLyrics] = useState({ status: "idle", lines: [], selected: null });
  const [lyricsCandidates, setLyricsCandidates] = useState([]);
  const [selectedLyricsId, setSelectedLyricsId] = useState("");
  const [lyricsQuery, setLyricsQuery] = useState("");
  const [activeLyricsQuery, setActiveLyricsQuery] = useState("");
  const [searchingLyrics, setSearchingLyrics] = useState(false);
  const [addTargetSong, setAddTargetSong] = useState(null);
  const [status, setStatus] = useState("");
  const apiHealth = useApiHealth(API_BASE);
  const pwaInstall = usePwaInstall();
  const audioRef = useRef(null);
  const inputRef = useRef(null);
  const playerTitleRef = useRef(null);
  const resultsHeadingRef = useRef(null);
  const miniPlayerRef = useRef(null);
  const playbackSequenceRef = useRef({ songs: [], origin: "search" });
  const playbackRequestRef = useRef(0);
  const preparedNextRef = useRef(null);
  const preparedAudioRef = useRef(null);
  const lyricsViewportRef = useRef(null);
  const activeLyricRef = useRef(null);
  const reportedFailureCandidatesRef = useRef(new Set());
  const reportedPlayingCandidatesRef = useRef(new Set());

  async function reportPlaybackEvent(event, details = {}) {
    if (!session?.sessionId || !candidate) return null;
    const requestId = playbackRequestRef.current;
    const currentCandidateId = candidateId(candidate);
    const eventId = crypto.randomUUID();
    const payload = {
      sessionId: session.sessionId,
      event,
      eventId,
      occurredAt: new Date().toISOString(),
      candidateId: currentCandidateId,
      sourceId: candidate.sourceId || "",
      provider: candidate.provider || "",
      decisionId: session.decisionId || "",
      correlationId: session.correlation?.correlationId || session.correlationId || "",
      positionMs: Math.round((audioRef.current?.currentTime || 0) * 1000),
      ...details,
    };

    try {
      const response = await fetch(`${API_BASE}/api/v1/playback/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Idempotency-Key": eventId,
        },
        body: JSON.stringify(payload),
      });
      const body = await response.json().catch(() => ({}));
      if (requestId !== playbackRequestRef.current) return null;
      if (!response.ok || !body?.success) return null;
      const result = body.data || {};
      if (result.action === "switch_source" && result.nextCandidate) {
        setCandidate(result.nextCandidate);
        setStatus(`正在切換至 ${providerLabel(result.nextCandidate.provider)}…`);
      }
      return result;
    } catch {
      // Feedback 不得阻塞或中斷播放器；服務端可由後續事件補足狀態。
      return null;
    }
  }

  function reportPlaybackFailure(event, details = {}) {
    const id = candidateId(candidate);
    if (!id || reportedFailureCandidatesRef.current.has(id)) return;
    reportedFailureCandidatesRef.current.add(id);
    void reportPlaybackEvent(event, details);
  }

  function reportPlaybackStarted() {
    const id = candidateId(candidate);
    if (!id || reportedPlayingCandidatesRef.current.has(id)) return;
    reportedPlayingCandidatesRef.current.add(id);
    void reportPlaybackEvent("playing");
  }

  // 手機完整播放器是覆蓋層：開啟時鎖定背景，關閉後還原原本捲動設定。
  useEffect(() => {
    if (!playerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    playerTitleRef.current?.focus({ preventScroll: true });
    const closeOnEscape = (event) => {
      if (event.key === "Escape" && !document.querySelector("dialog[open]")) {
        setPlayerOpen(false);
        miniPlayerRef.current?.focus({ preventScroll: true });
      }
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [playerOpen]);

  useEffect(() => {
    if (!pendingUndo) return;
    const timeout = window.setTimeout(() => setPendingUndo(null), 7000);
    return () => window.clearTimeout(timeout);
  }, [pendingUndo]);

  useEffect(() => {
    if (!status || status.startsWith("正在")) return;
    const timeout = window.setTimeout(() => setStatus(""), 5000);
    return () => window.clearTimeout(timeout);
  }, [status]);

  const selectedPlaylist = playlists.find((item) => item.id === selectedPlaylistId) || playlists[0];
  const visibleSongs = activeTab === "favorites"
    ? favorites
    : activeTab === "playlists"
      ? selectedPlaylist?.songs || []
      : activeTab === "queue"
        ? queue
        : songs;
  const favoritesIds = useMemo(() => new Set(favorites.map((song) => song.recordingId)), [favorites]);
  const candidates = useMemo(() => sessionCandidates(session), [session]);
  const lyricRows = useMemo(() => parseLyricRows(lyrics.lines), [lyrics.lines]);
  const hasTimedLyrics = lyricRows.some((row) => row.time !== null);
  const activeLyricIndex = hasTimedLyrics
    ? Math.max(0, lyricRows.findLastIndex((row) => row.time !== null && row.time <= progress + 0.15))
    : -1;
  const displayedLyricRows = lyricRows;

  useEffect(() => {
    if (!showLyrics || !hasTimedLyrics || activeLyricIndex < 0) return;
    const frame = requestAnimationFrame(() => {
      const viewport = lyricsViewportRef.current;
      const activeLine = activeLyricRef.current;
      if (!viewport || !activeLine) return;
      const top = activeLine.offsetTop
        - viewport.clientHeight / 2
        + activeLine.offsetHeight / 2;
      viewport.scrollTo({ top: Math.max(0, top), behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
    });
    return () => cancelAnimationFrame(frame);
  }, [showLyrics, hasTimedLyrics, activeLyricIndex, selectedLyricsId]);

  useEffect(() => {
    if (!session || !candidate || candidate.playbackMode !== "direct_audio") {
      preparedAudioRef.current = null;
      setAudioUrl("");
      setLoadingAudio(false);
      return;
    }
    const preparedAudio = preparedAudioRef.current;
    if (preparedAudio?.sessionId === session.sessionId
      && preparedAudio.candidateId === candidateId(candidate)) {
      preparedAudioRef.current = null;
      setLoadingAudio(false);
      setAudioUrl(preparedAudio.audioUrl);
      setStatus(`${providerLabel(candidate.provider)} 音訊來源已預檢`);
      return;
    }
    preparedAudioRef.current = null;
    const controller = new AbortController();
    let disposed = false;
    async function prepare() {
      setLoadingAudio(true);
      setAudioUrl("");
      setStatus(`正在準備 ${providerLabel(candidate.provider)} 音訊…`);
      try {
        const response = await fetch(streamUrl(session, candidate), {
          signal: controller.signal,
          headers: { Accept: "audio/*,*/*;q=0.1", Range: "bytes=0-0" },
        });
        if (!response.ok && response.status !== 206) {
          const body = await response.json().catch(() => ({}));
          if (body?.error?.details?.nextCandidate) {
            setCandidate(body.error.details.nextCandidate);
            return;
          }
          throw new Error(userMessage(body, "音訊暫時無法載入"));
        }
        const resolvedId = response.headers.get("X-Music-Life-Candidate-Id");
        const resolvedSource = response.headers.get("X-Music-Life-Source-Id");
        const resolved = candidates.find((item) =>
          (resolvedId && candidateId(item) === resolvedId) ||
          (resolvedSource && item.sourceId === resolvedSource)
        ) || candidate;
        await response.body?.cancel().catch(() => {});
        if (disposed) return;
        setCandidate(resolved);
        setAudioUrl(streamUrl(session, resolved));
        setStatus(`${providerLabel(resolved.provider)} 音訊已就緒`);
      } catch (error) {
        if (error?.name !== "AbortError" && !disposed) setStatus(error?.message || "音訊準備失敗");
      } finally {
        if (!disposed) setLoadingAudio(false);
      }
    }
    prepare();
    return () => { disposed = true; controller.abort(); };
  }, [session?.sessionId, candidateId(candidate)]);

  useEffect(() => {
    if (!candidate) return;
    reportedFailureCandidatesRef.current.delete(candidateId(candidate));
  }, [candidateId(candidate)]);

  useEffect(() => {
    reportedFailureCandidatesRef.current.clear();
    reportedPlayingCandidatesRef.current.clear();
  }, [session?.sessionId]);

  // 目前歌曲開始播放後，只準備下一首；暫停不丟棄已完成的結果。
  useEffect(() => {
    if (!playing || !currentSong || playbackMode === "one") return;
    const sequence = playbackSequenceRef.current;
    const tracks = sequence.origin === "queue" ? queue : sequence.songs;
    const nextSong = songForPlaybackMode(
      tracks,
      currentSong.recordingId,
      1,
      playbackMode
    );
    if (!nextSong || nextSong.recordingId === currentSong.recordingId) return;

    const existing = preparedNextRef.current;
    if (existing?.fromRecordingId === currentSong.recordingId
      && existing.mode === playbackMode
      && preparedPlaybackIsUsable(existing, existing.song)) return;

    preparedNextRef.current = null;
    const controller = new AbortController();
    let disposed = false;
    if (nextSong.thumbnailUrl) {
      const artwork = new Image();
      artwork.decoding = "async";
      artwork.src = nextSong.thumbnailUrl;
    }

    void prepareNextPlayback(nextSong, sequence.origin, controller.signal)
      .then((prepared) => {
        if (disposed) return;
        if (prepared.song.thumbnailUrl && prepared.song.thumbnailUrl !== nextSong.thumbnailUrl) {
          const artwork = new Image();
          artwork.decoding = "async";
          artwork.src = prepared.song.thumbnailUrl;
        }
        preparedNextRef.current = {
          ...prepared,
          fromRecordingId: currentSong.recordingId,
          mode: playbackMode,
        };
      })
      .catch((error) => {
        if (error?.name !== "AbortError" && !disposed) preparedNextRef.current = null;
      });

    return () => {
      disposed = true;
      controller.abort();
    };
  }, [playing, currentSong?.recordingId, playbackMode, session?.sessionId]);

  useEffect(() => {
    if (!showLyrics || !currentSong?.playbackRef) return;
    const controller = new AbortController();
    const choices = readStore(STORE.lyricChoices, {});
    const remembered = choices[lyricChoiceKey(currentSong)];
    const rememberedQuery = text(remembered?.query);
    setLyricsQuery(rememberedQuery || currentSong.title || "");
    setLyrics({ status: "loading", lines: [], selected: null });
    setSearchingLyrics(true);
    const params = new URLSearchParams({ playbackRef: currentSong.playbackRef });
    if (rememberedQuery) params.set("q", rememberedQuery);
    fetch(`${API_BASE}/api/v1/music/lyrics/candidates?${params}`, {
      signal: controller.signal,
    })
      .then(async (response) => ({ response, body: await response.json().catch(() => ({})) }))
      .then(({ response, body }) => {
        if (controller.signal.aborted) return;
        const candidates = response.ok && body?.success ? body?.data?.candidates || [] : [];
        const selected = candidates.find((item) => item.candidateId === remembered?.candidateId)
          || candidates.find((item) => item.candidateId === body?.data?.defaultCandidateId)
          || candidates[0];
        if (remembered?.candidateId &&
            !candidates.some((item) => item.candidateId === remembered.candidateId)) {
          const nextChoices = { ...choices };
          delete nextChoices[lyricChoiceKey(currentSong)];
          localStorage.setItem(STORE.lyricChoices, JSON.stringify(nextChoices));
        }
        if (body?.data?.artwork?.url) {
          setCurrentSong((song) => song?.recordingId === body.data.recordingId
            ? { ...song, thumbnailUrl: body.data.artwork.url }
            : song
          );
        }
        setActiveLyricsQuery(body?.data?.query || rememberedQuery || "");
        setLyricsCandidates(candidates);
        setSelectedLyricsId(selected?.candidateId || "");
        setLyrics(selected?.lyrics?.length
          ? { status: "ready", lines: selected.lyrics, selected }
          : { status: "empty", lines: [], selected: null });
      })
      .catch((error) => {
        if (error?.name !== "AbortError") {
          setLyricsCandidates([]);
          setSelectedLyricsId("");
          setLyrics({ status: "empty", lines: [], selected: null });
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setSearchingLyrics(false);
      });
    return () => controller.abort();
  }, [showLyrics, currentSong?.playbackRef]);

  function selectLyrics(candidate) {
    if (!candidate?.lyrics?.length || !currentSong) return;
    setSelectedLyricsId(candidate.candidateId);
    setLyrics({ status: "ready", lines: candidate.lyrics, selected: candidate });
    const choices = readStore(STORE.lyricChoices, {});
    localStorage.setItem(STORE.lyricChoices, JSON.stringify({
      ...choices,
      [lyricChoiceKey(currentSong)]: {
        candidateId: candidate.candidateId,
        provider: candidate.provider,
        providerTrackId: candidate.providerTrackId,
        query: activeLyricsQuery,
      },
    }));
  }

  async function searchLyrics(event) {
    event?.preventDefault?.();
    if (!currentSong?.playbackRef || searchingLyrics) return;
    const query = text(lyricsQuery);
    setSearchingLyrics(true);
    setLyrics({ status: "loading", lines: [], selected: null });
    try {
      const params = new URLSearchParams({ playbackRef: currentSong.playbackRef });
      if (query) params.set("q", query);
      const response = await fetch(`${API_BASE}/api/v1/music/lyrics/candidates?${params}`);
      const body = await response.json().catch(() => ({}));
      const candidates = response.ok && body?.success ? body?.data?.candidates || [] : [];
      const selected = candidates.find((item) => item.candidateId === body?.data?.defaultCandidateId)
        || candidates[0];
      if (body?.data?.artwork?.url) {
        setCurrentSong((song) => song?.recordingId === body.data.recordingId
          ? { ...song, thumbnailUrl: body.data.artwork.url }
          : song
        );
      }
      setActiveLyricsQuery(body?.data?.query || query);
      setLyricsCandidates(candidates);
      setSelectedLyricsId(selected?.candidateId || "");
      setLyrics(selected?.lyrics?.length
        ? { status: "ready", lines: selected.lyrics, selected }
        : { status: "empty", lines: [], selected: null });
    } catch {
      setLyricsCandidates([]);
      setSelectedLyricsId("");
      setLyrics({ status: "empty", lines: [], selected: null });
    } finally {
      setSearchingLyrics(false);
    }
  }

  async function search(event) {
    event?.preventDefault?.();
    const query = text(keyword);
    if (searching) return;
    if (!query) { inputRef.current?.focus(); return; }
    if (!selectedSources.length) {
      setStatus("請至少選擇一個音源");
      return;
    }
    setSearching(true);
    setSearchState("loading");
    setSearchedKeyword(query);
    setActiveTab("search");
    setStatus("正在搜尋所有可用音源…");
    try {
      const params = new URLSearchParams({
        q: query,
        limit: String(resultLimit),
        market: MARKET,
        language: "zh-TW",
        providers: selectedSources.join(","),
      });
      const response = await fetch(`${API_BASE}/api/v1/music/search?${params}`);
      const body = await response.json();
      if (!response.ok || !body?.success) throw new Error(userMessage(body, "搜尋失敗"));
      setSongs(body.data?.results || []);
      setSearchState("ready");
      setStatus(`找到 ${body.data?.results?.length || 0} 首歌曲`);
      requestAnimationFrame(() => resultsHeadingRef.current?.focus({ preventScroll: true }));
    } catch (error) {
      setSongs([]);
      setSearchState("error");
      setStatus(error?.message || "搜尋失敗，請稍後再試");
    } finally { setSearching(false); }
  }

  function replaceStoredSong(savedSong, refreshedSong) {
    const replace = (item) => item.recordingId === savedSong.recordingId
      ? mergeRefreshedSong(item, refreshedSong)
      : item;
    // 過期資料刷新可能更新歌曲識別碼，播放順序也要同步，避免下一首回到清單開頭。
    playbackSequenceRef.current = {
      ...playbackSequenceRef.current,
      songs: playbackSequenceRef.current.songs.map(replace),
    };
    setSongs((items) => items.map(replace));
    setFavorites((items) => items.map(replace));
    setQueue((items) => items.map(replace));
    setPlaylists((items) => items.map((playlist) => ({
      ...playlist,
      songs: playlist.songs.map(replace),
    })));
  }

  async function refreshStoredSong(song, { silent = false, signal } = {}) {
    if (!silent) setStatus("正在更新已儲存歌曲的播放資料…");
    const query = [song.title, ...(song.artists || [])].filter(Boolean).join(" ");
    const params = new URLSearchParams({
      q: query,
      limit: "50",
      market: MARKET,
      language: "zh-TW",
      providers: selectedSources.join(","),
    });
    const response = await fetch(`${API_BASE}/api/v1/music/search?${params}`, { signal });
    const body = await response.json().catch(() => ({}));
    if (!response.ok || !body?.success) {
      throw new Error(userMessage(body, "無法更新歌曲播放資料"));
    }
    const refreshed = selectPlaybackRefreshMatch(song, body.data?.results || []);
    if (!refreshed?.playbackRef) {
      throw new Error("找不到足夠吻合的歌曲版本，請重新搜尋後加入");
    }
    const updatedSong = mergeRefreshedSong(song, refreshed);
    replaceStoredSong(song, updatedSong);
    return updatedSong;
  }

  async function requestPlaybackStart(song, signal) {
    const response = await fetch(`${API_BASE}/api/v1/playback/start`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      signal,
      body: JSON.stringify({
        recordingId: song.recordingId,
        playbackRef: song.playbackRef,
        market: MARKET,
        supportedPlaybackModes: ["direct_audio", "official_embed"],
      }),
    });
    return { response, body: await response.json().catch(() => ({})) };
  }

  /** Range 只取第一個位元組，用來驗證音訊與備援，不下載整首歌曲。 */
  async function preflightAudio(sessionData, initialCandidate, signal) {
    let selected = initialCandidate;
    const attempted = new Set();
    while (selected?.playbackMode === "direct_audio" && !attempted.has(candidateId(selected))) {
      attempted.add(candidateId(selected));
      const response = await fetch(streamUrl(sessionData, selected), {
        signal,
        headers: { Accept: "audio/*,*/*;q=0.1", Range: "bytes=0-0" },
      });
      if (response.ok || response.status === 206) {
        const resolvedId = response.headers.get("X-Music-Life-Candidate-Id");
        const resolvedSource = response.headers.get("X-Music-Life-Source-Id");
        const resolved = sessionCandidates(sessionData).find((item) =>
          (resolvedId && candidateId(item) === resolvedId)
          || (resolvedSource && item.sourceId === resolvedSource)
        ) || selected;
        await response.body?.cancel().catch(() => {});
        return { candidate: resolved, audioUrl: streamUrl(sessionData, resolved) };
      }
      const body = await response.json().catch(() => ({}));
      const fallback = body?.error?.details?.nextCandidate;
      if (!fallback) throw new Error(userMessage(body, "下一首音訊預檢失敗"));
      selected = fallback;
    }
    return { candidate: selected, audioUrl: "" };
  }

  /** 背景刷新歌曲、建立 Session 並確認首選音訊，結果只保留給下一次切歌。 */
  async function prepareNextPlayback(song, origin, signal) {
    let playableSong = song;
    let refreshed = false;
    if (!playableSong.playbackRef || playbackRefNeedsRefresh(playableSong.playbackRef)) {
      playableSong = await refreshStoredSong(playableSong, { silent: true, signal });
      refreshed = true;
    }
    let { response, body } = await requestPlaybackStart(playableSong, signal);
    if (!response.ok && shouldRefreshPlaybackData(body, origin) && !refreshed) {
      playableSong = await refreshStoredSong(playableSong, { silent: true, signal });
      ({ response, body } = await requestPlaybackStart(playableSong, signal));
    }
    if (!response.ok || !body?.success || !body?.data?.selected) {
      throw new Error(userMessage(body, "下一首歌曲預先檢查失敗"));
    }
    const sessionData = body.data;
    const media = await preflightAudio(sessionData, sessionData.selected, signal);
    return { song: playableSong, session: sessionData, ...media };
  }

  // 保存開始播放時的清單；切換瀏覽分頁不改變下一首。
  async function startSong(song, sequence = visibleSongs, origin = activeTab, prepared = null) {
    if (!song) return;
    const cached = preparedPlaybackIsUsable(prepared, song) ? prepared : null;
    const initialSong = cached?.song || song;
    const requestId = ++playbackRequestRef.current;
    playbackSequenceRef.current = { songs: sequence.length ? sequence : [song], origin };
    audioRef.current?.pause();
    setPlaying(false);
    setCurrentSong(initialSong);
    setShowLyrics(false);
    setLyricsCandidates([]);
    setSelectedLyricsId("");
    setLyricsQuery(initialSong.title || "");
    setActiveLyricsQuery("");
    setLyrics({ status: "idle", lines: [], selected: null });
    setProgress(0);
    setDuration(0);
    setLoadingRef(initialSong.playbackRef || initialSong.recordingId || "refreshing");
    setSession(null);
    setCandidate(null);
    setAudioUrl("");
    setStatus("正在驗證歌曲與可播放來源…");
    try {
      if (cached) {
        if (cached.audioUrl) {
          preparedAudioRef.current = {
            sessionId: cached.session.sessionId,
            candidateId: candidateId(cached.candidate),
            audioUrl: cached.audioUrl,
          };
        }
        setSession(cached.session);
        setCandidate(cached.candidate);
        setAudioUrl(cached.audioUrl || "");
        if (cached.candidate.playbackMode === "official_embed") setPlayerOpen(true);
        setStatus(`已預先準備 ${providerLabel(cached.candidate.provider)}`);
        return;
      }
      let playableSong = song;
      let refreshed = false;
      // 舊歌單可能沒有 playbackRef；與過期資料一樣先重新搜尋安全匹配版本。
      if (!playableSong.playbackRef || playbackRefNeedsRefresh(playableSong.playbackRef)) {
        playableSong = await refreshStoredSong(playableSong);
        if (requestId !== playbackRequestRef.current) return;
        setCurrentSong(playableSong);
        setLoadingRef(playableSong.playbackRef);
        refreshed = true;
      }
      let { response, body } = await requestPlaybackStart(playableSong);
      if (requestId !== playbackRequestRef.current) return;
      // 已儲存歌曲若解析不到來源，刷新映射後只重試一次，避免無限請求。
      if (!response.ok && shouldRefreshPlaybackData(body, origin) && !refreshed) {
        playableSong = await refreshStoredSong(playableSong);
        if (requestId !== playbackRequestRef.current) return;
        setCurrentSong(playableSong);
        setLoadingRef(playableSong.playbackRef);
        ({ response, body } = await requestPlaybackStart(playableSong));
        if (requestId !== playbackRequestRef.current) return;
      }
      if (!response.ok || !body?.success || !body?.data?.selected) {
        throw new Error(userMessage(body, "目前沒有可播放來源"));
      }
      setSession(body.data);
      setCandidate(body.data.selected);
      if (body.data.selected.playbackMode === "official_embed") setPlayerOpen(true);
      const resolvedThumbnail = body.data.selected.thumbnailUrl
        || body.data.selected.metadata?.thumbnailUrl;
      if (resolvedThumbnail) {
        setCurrentSong((current) => current?.recordingId === playableSong.recordingId
          ? { ...current, thumbnailUrl: resolvedThumbnail }
          : current
        );
      }
      setStatus(`已選擇 ${providerLabel(body.data.selected.provider)}`);
    } catch (error) {
      if (requestId === playbackRequestRef.current) setStatus(error?.message || "暫時無法播放");
    } finally {
      if (requestId === playbackRequestRef.current) setLoadingRef("");
    }
  }

  function toggleFavorite(song) {
    const index = favorites.findIndex((item) => item.recordingId === song.recordingId);
    if (index >= 0) {
      setFavorites((items) => items.filter((item) => item.recordingId !== song.recordingId));
      setPendingUndo({ scope: "favorites", song, index, message: `已取消收藏：${song.title || "未命名歌曲"}` });
      setStatus("");
      return;
    }
    setPendingUndo(null);
    setFavorites((items) => [song, ...items]);
    setStatus(`已收藏：${song.title || "未命名歌曲"}`);
  }
  function addToFavorites(song) {
    if (favorites.some((item) => item.recordingId === song.recordingId)) {
      setStatus(`「${song.title || "這首歌"}」已在我的收藏`);
      return;
    }
    setPendingUndo(null);
    setFavorites((items) => [song, ...items]);
    setStatus(`已加入我的收藏：${song.title || "未命名歌曲"}`);
  }
  function addToQueue(song) {
    if (queue.some((item) => item.recordingId === song.recordingId)) {
      setStatus(`「${song.title || "這首歌"}」已在播放列表`);
      return;
    }
    setPendingUndo(null);
    setQueue((items) => [...items, song]);
    setStatus(`已加入播放列表：${song.title || "未命名歌曲"}`);
  }
  function addToPlaylist(song, playlistId = selectedPlaylistId) {
    const target = playlists.find((item) => item.id === playlistId) || playlists[0];
    if (!target) return;
    if (target.songs.some((entry) => entry.recordingId === song.recordingId)) {
      setStatus(`「${song.title || "這首歌"}」已在「${target.name}」`);
      return;
    }
    setPendingUndo(null);
    setPlaylists((items) => items.map((item) =>
      item.id === target.id ? { ...item, songs: [song, ...item.songs] } : item
    ));
    setStatus(`已加入「${target.name}」`);
  }
  function removeVisibleSong(song) {
    if (activeTab === "queue") {
      const index = queue.findIndex((item) => item.recordingId === song.recordingId);
      setQueue((items) => items.filter((item) => item.recordingId !== song.recordingId));
      setPendingUndo({ scope: "queue", song, index, message: `已從播放列表移除：${song.title || "未命名歌曲"}` });
      setStatus("");
      return;
    }
    if (activeTab === "favorites") {
      const index = favorites.findIndex((item) => item.recordingId === song.recordingId);
      setFavorites((items) => items.filter((item) => item.recordingId !== song.recordingId));
      setPendingUndo({ scope: "favorites", song, index, message: `已從我的收藏移除：${song.title || "未命名歌曲"}` });
      setStatus("");
      return;
    }
    if (activeTab === "playlists" && selectedPlaylist) {
      const playlistId = selectedPlaylist.id;
      const index = selectedPlaylist.songs.findIndex((item) => item.recordingId === song.recordingId);
      setPlaylists((items) => items.map((item) =>
        item.id === playlistId
          ? { ...item, songs: item.songs.filter((entry) => entry.recordingId !== song.recordingId) }
          : item
      ));
      setPendingUndo({ scope: "playlist", playlistId, song, index, message: `已從「${selectedPlaylist.name}」移除：${song.title || "未命名歌曲"}` });
      setStatus("");
    }
  }

  function restoreRemovedSong() {
    if (!pendingUndo) return;
    const { scope, song, index, playlistId } = pendingUndo;
    if (scope === "queue") setQueue((items) => restoreSongAt(items, song, index));
    if (scope === "favorites") setFavorites((items) => restoreSongAt(items, song, index));
    if (scope === "playlist") {
      if (!playlists.some((item) => item.id === playlistId)) {
        setPendingUndo(null);
        setStatus("原歌單已刪除，無法復原歌曲");
        return;
      }
      setPlaylists((items) => items.map((item) => item.id === playlistId
        ? { ...item, songs: restoreSongAt(item.songs, song, index) }
        : item));
    }
    setPendingUndo(null);
    setStatus(`已復原：${song.title || "未命名歌曲"}`);
  }
  function createPlaylist() {
    setPlaylistEditor({ mode: "create", name: "", song: null });
  }
  function createPlaylistWithSong(song) {
    setAddTargetSong(null);
    setPlaylistEditor({ mode: "create", name: "", song });
  }
  function renamePlaylist() {
    if (selectedPlaylist) setPlaylistEditor({ mode: "rename", id: selectedPlaylist.id, name: selectedPlaylist.name });
  }
  function savePlaylist(event) {
    event.preventDefault();
    const name = text(playlistEditor?.name);
    if (!name) return;
    if (playlistEditor.mode === "rename") {
      setPlaylists((items) => items.map((item) => item.id === playlistEditor.id ? { ...item, name } : item));
      setStatus(`歌單已重新命名為「${name}」`);
    } else {
      const id = `playlist-${crypto.randomUUID()}`;
      setPlaylists((items) => [...items, { id, name, songs: playlistEditor.song ? [playlistEditor.song] : [] }]);
      setSelectedPlaylistId(id);
      if (!playlistEditor.song) openLibrary("playlists");
      setStatus(`已建立「${name}」${playlistEditor.song ? "並加入歌曲" : ""}`);
    }
    setPlaylistEditor(null);
  }
  function deletePlaylist() {
    if (selectedPlaylist) setDeletePlaylistTarget(selectedPlaylist);
  }
  function confirmDeletePlaylist() {
    if (!deletePlaylistTarget) return;
    const targetId = deletePlaylistTarget.id;
    if (playlists.length === 1) {
      setPlaylists([{ id: "default", name: "我的歌單", songs: [] }]);
      setSelectedPlaylistId("default");
    } else {
      const remaining = playlists.filter((item) => item.id !== targetId);
      setPlaylists(remaining);
      setSelectedPlaylistId(remaining[0].id);
    }
    setDeletePlaylistTarget(null);
    setStatus("自建歌單已刪除");
  }
  function move(direction) {
    const sequence = playbackSequenceRef.current;
    const tracks = sequence.origin === "queue" ? queue : sequence.songs;
    const prepared = direction > 0 ? preparedNextRef.current : null;
    const preparedTrack = tracks.find((song) => song.recordingId === prepared?.song?.recordingId);
    const usePrepared = prepared?.fromRecordingId === currentSong?.recordingId
      && prepared.mode === playbackMode
      && preparedPlaybackIsUsable(prepared, preparedTrack);
    const next = usePrepared
      ? prepared.song
      : songForPlaybackMode(tracks, currentSong?.recordingId, direction, playbackMode);
    if (!next) return;
    if (usePrepared) preparedNextRef.current = null;
    void startSong(next, tracks, sequence.origin, usePrepared ? prepared : null);
  }
  function cyclePlaybackMode() {
    const nextMode = nextPlaybackMode(playbackMode);
    setPlaybackMode(nextMode);
    setStatus(`播放模式：${PLAYBACK_MODE_META[nextMode].label}`);
  }
  async function togglePlayback() {
    const audio = audioRef.current;
    if (!audio || loadingAudio || loadingRef) return;
    try {
      if (audio.paused) await audio.play();
      else audio.pause();
    } catch {
      setPlaying(false);
      setStatus("播放未能開始，請再次點擊播放");
    }
  }
  function playRow(song) {
    if (currentSong?.recordingId === song.recordingId && audioUrl) void togglePlayback();
    else void startSong(song);
  }
  function openLibrary(tab = activeTab, focusSearch = false) {
    setActiveTab(tab);
    setPlayerOpen(false);
    if (focusSearch) requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
  }
  function closePlayer() {
    setPlayerOpen(false);
    requestAnimationFrame(() => miniPlayerRef.current?.focus({ preventScroll: true }));
  }

  function toggleSource(providerId) {
    if (selectedSources.includes(providerId) && selectedSources.length === 1) {
      setStatus("請至少保留一個音源");
      return;
    }
    setSelectedSources((items) => items.includes(providerId)
      ? items.filter((id) => id !== providerId)
      : [...items, providerId]);
  }
  function selectApiSource(choice) {
    const nextSources = selectionForSourceChoice(choice, SOURCE_IDS);
    setSelectedSources(nextSources);
    const selected = SOURCE_OPTIONS.find((source) => source.id === choice);
    setStatus(`API 來源：${selected ? `${selected.label} 單一鏡像` : "自動多來源"}`);
  }
  async function installPwa() {
    const outcome = await pwaInstall.install();
    if (outcome === "accepted") setStatus("Music Life 已安裝");
    else if (outcome === "dismissed") setStatus("已取消安裝，可稍後再試");
    else if (outcome === "failed") setStatus("安裝提示暫時無法開啟，請使用瀏覽器選單安裝");
  }

  const favorite = Boolean(currentSong && favoritesIds.has(currentSong.recordingId));
  const busy = Boolean(loadingRef || loadingAudio);
  const playable = Boolean(audioUrl && !busy);
  const sequence = playbackSequenceRef.current;
  const hasSequence = (sequence.origin === "queue" ? queue : sequence.songs).length > 0;
  const playbackModeMeta = PLAYBACK_MODE_META[playbackMode];
  const apiHealthSummary = aggregateApiHealth(apiHealth.data, SOURCE_IDS);
  const pageTitle = PAGE_TITLES[activeTab];
  const navigationButtons = NAVIGATION.map(([id, label, icon]) => (
    <button key={id} type="button" className={activeTab === id ? "active" : ""}
      aria-current={activeTab === id ? "page" : undefined} onClick={() => openLibrary(id)}>
      <Icon name={icon} size={21}/><span>{label}</span>
    </button>
  ));
  return (
    <div className={"app" + (currentSong ? " has-track" : "") + (playerOpen ? " is-player-open" : "")}>
      <a className="skip-link" href="#library">前往音樂列表</a>
      <header className="header">
        <button className="brand" type="button" onClick={() => openLibrary("search")}>
          <img className="brand-icon" src="/icons/music-life-192.png" alt="" aria-hidden="true"/>
          <b>Music Life</b>
        </button>
        <nav className="desktop-nav" aria-label="主要導覽">{navigationButtons}</nav>
        <button type="button" className={"icon-button header-queue " + (activeTab === "queue" ? "active" : "")}
          onClick={() => openLibrary("queue")} aria-label={"播放列表，" + queue.length + " 首"}>
          <Icon name="list"/><span>{queue.length}</span>
        </button>
        <button type="button" className="icon-button header-settings" onClick={() => setSettingsOpen(true)}
          aria-label={`開啟設定；API ${apiHealthSummary.label}`}>
          <Icon name="settings"/><span className="header-health-dot health-dot" data-tone={apiHealthSummary.tone}/>
        </button>
      </header>

      <main>
        <section id="library" className="library" aria-label={pageTitle}>
          <div className="library-head">
            <h1>{pageTitle}</h1>
            {activeTab === "playlists" && <button type="button" className="text-button" onClick={createPlaylist}>
              <Icon name="plus" size={18}/>新增歌單
            </button>}
          </div>
          {activeTab === "search" && <>
            <form className="search-form" role="search" onSubmit={search}>
              <Icon name="search"/>
              <input ref={inputRef} value={keyword} onChange={(event) => setKeyword(event.target.value)}
                type="search" enterKeyHint="search" aria-label="搜尋歌曲、歌手或專輯"
                placeholder="歌曲、歌手或專輯" autoComplete="off" />
              <button type="submit" className="primary-button" disabled={searching}>
                {searching ? <><span className="spinner"/>搜尋中</> : "搜尋"}
              </button>
            </form>
            <details className="source-picker">
              <summary><span>搜尋設定</span><small>{selectedSources.length} 個音源 · {resultLimit} 首</small><Icon name="chevronDown" size={16}/></summary>
              <div className="source-picker__body">
                <span className="source-picker__title">搜尋音源</span>
                <div className="source-options">
                  {SOURCE_OPTIONS.map((source) => (
                    <button key={source.id} type="button" className={"source-option " + (selectedSources.includes(source.id) ? "active" : "")}
                      onClick={() => toggleSource(source.id)} aria-pressed={selectedSources.includes(source.id)}>
                      <span className="source-check" aria-hidden="true">{selectedSources.includes(source.id) ? "✓" : "+"}</span>{source.label}
                    </button>
                  ))}
                </div>
                <label className="search-settings">結果數量
                  <select value={resultLimit} onChange={(event) => setResultLimit(Number(event.target.value))}>
                    <option value="10">10 首</option><option value="25">25 首</option><option value="50">50 首</option>
                  </select>
                </label>
              </div>
            </details>
          </>}

          {activeTab === "playlists" && <div className="playlist-manager">
            <div className="playlist-chips" aria-label="選擇歌單">
              {playlists.map((item) => <button key={item.id} type="button"
                className={selectedPlaylist?.id === item.id ? "active" : ""}
                aria-pressed={selectedPlaylist?.id === item.id} onClick={() => setSelectedPlaylistId(item.id)}>
                <Icon name="music" size={18}/><span>{item.name}</span><small>{item.songs.length}</small>
              </button>)}
            </div>
            <div className="playlist-actions">
              <button type="button" className="text-button" onClick={renamePlaylist}>重新命名</button>
              <button type="button" className="text-button danger" onClick={deletePlaylist}>刪除歌單</button>
            </div>
          </div>}

          <div className="list-toolbar">
            <h2 ref={resultsHeadingRef} tabIndex={-1}>{activeTab === "search" ? (searchState === "idle" ? "歌曲" : "搜尋結果") : activeTab === "playlists" ? selectedPlaylist?.name : "歌曲"}
              <span>{searching && activeTab === "search" ? "搜尋中" : visibleSongs.length + " 首"}</span>
            </h2>
            <button type="button" className="text-button" disabled={!visibleSongs.length || busy || searching}
              onClick={() => startSong(visibleSongs[0])}><Icon name="play" size={17}/>全部播放</button>
          </div>
          <div className="song-list" aria-busy={activeTab === "search" && searching}>
            {activeTab === "search" && searching ? <div className="search-loading">
              <span className="sr-only" role="status">正在尋找「{searchedKeyword}」</span>
              {SEARCH_SKELETON_ROWS.map((item) => <div className="skeleton-row" key={item} aria-hidden="true">
                <span className="skeleton-art"/><span className="skeleton-copy"><i/><i/></span>
              </div>)}
            </div> : visibleSongs.length ? visibleSongs.map((song, index) =>
              <SongRow key={song.recordingId + "-" + index} song={song}
                active={currentSong?.recordingId === song.recordingId} playing={playing}
                loading={Boolean(loadingRef) && loadingRef === (song.playbackRef || song.recordingId)}
                favorite={favoritesIds.has(song.recordingId)}
                onPlay={() => playRow(song)} onAdd={() => setAddTargetSong(song)}
                onRemove={activeTab !== "search" ? () => removeVisibleSong(song) : null} />)
              : <div className="empty">
                  <span className="empty-icon"><Icon name={activeTab === "favorites" ? "heart" : activeTab === "search" ? "search" : "music"} size={30}/></span>
                  <strong>{activeTab === "search"
                    ? searchState === "error" ? "搜尋暫時無法完成" : searchState === "ready" ? "沒有找到相符歌曲" : "今天想聽什麼？"
                    : activeTab === "queue" ? "播放列表還是空的" : activeTab === "favorites" ? "還沒有收藏歌曲" : "這個歌單還是空的"}</strong>
                  <p>{activeTab === "search"
                    ? searchState === "error" ? "請稍後重試，或調整搜尋音源。" : searchState === "ready" ? "試試其他歌名、歌手或音源。" : "輸入歌名或歌手，找到後點一下就能播放。"
                    : "找到喜歡的歌曲，點擊＋加入這裡。"}</p>
                  {activeTab === "search" && searchState === "error" && <button type="button" className="primary-button" onClick={search}>重新搜尋</button>}
                  {activeTab === "search" && searchState === "ready" && <button type="button" className="text-button" onClick={() => inputRef.current?.focus()}>調整關鍵字</button>}
                  {activeTab !== "search" && <button type="button" className="primary-button" onClick={() => openLibrary("search", true)}>搜尋歌曲</button>}
                </div>}
          </div>
        </section>

        <section className="player" aria-label="正在播放">
          <div className="player-head">
            <button type="button" className="icon-button player-close" onClick={closePlayer} aria-label="收起播放器"><Icon name="chevronDown"/></button>
            <span>正在播放</span>
            <button type="button" className="icon-button" disabled={!currentSong} onClick={() => setAddTargetSong(currentSong)}
              aria-label="選擇加入位置" aria-haspopup="dialog"><Icon name="plus"/></button>
          </div>
          <div className="visual-wrap">
            <div className={"cover-panel " + (showLyrics ? "show-lyrics" : "")}>
              <button type="button" className="cover-button" hidden={showLyrics}
                disabled={!currentSong} onClick={() => setShowLyrics(true)} aria-label="顯示歌詞">
                <Artwork song={currentSong} className="hero-art"/>
                {currentSong && <span className="cover-hint"><Icon name="lyrics" size={15}/>查看歌詞</span>}
              </button>
              {showLyrics && <div className="lyrics-card" role="region" aria-label="歌曲歌詞">
                <span className="lyrics-label"><Icon name="lyrics" size={16}/>歌詞</span>
                {lyrics.status === "loading" ? <span className="spinner"/> :
                  lyrics.status === "ready" ? <div className="lyrics-viewport" ref={lyricsViewportRef} tabIndex={0} aria-label="歌詞內容">
                    {displayedLyricRows.map((row) => {
                      const active = hasTimedLyrics && row === lyricRows[activeLyricIndex];
                      return <p key={row.id} ref={active ? activeLyricRef : null} className={active ? "is-active" : ""}>{row.text}</p>;
                    })}
                  </div> :
                  <div className="lyrics-empty"><strong>目前沒有可用歌詞</strong><small>可展開歌詞版本重新搜尋</small></div>}
                <button type="button" className="cover-hint" onClick={() => setShowLyrics(false)}>返回封面</button>
              </div>}
            </div>
          </div>
          <div className="track-title">
            <h2 ref={playerTitleRef} tabIndex={-1}>{currentSong?.title || "還沒開始播放"}</h2>
            <p>{currentSong ? (currentSong.artists || []).join("、") || "未知演出者" : "從左側選一首喜歡的音樂"}</p>
            {candidate && <small>{providerLabel(candidate.provider)} · {candidate.quality || "自動音質"}</small>}
          </div>

          {candidate?.playbackMode === "official_embed" && candidate.provider === "youtube" &&
            <iframe className="embed" title="YouTube 播放器" src={`https://www.youtube.com/embed/${candidate.embedId}?autoplay=1&playsinline=1`} allow="autoplay; encrypted-media"/>}
          {candidate?.playbackMode === "direct_audio" && audioUrl &&
            <audio ref={audioRef} src={audioUrl} autoPlay loop={playbackMode === "one"}
              onPlay={() => {
                setPlaying(true);
                reportPlaybackStarted();
              }}
              onPause={() => setPlaying(false)}
              onStalled={() => reportPlaybackFailure("stalled", {
                errorCategory: "TIMEOUT",
                errorCode: "PLAYBACK_STALLED",
              })}
              onError={() => reportPlaybackFailure("error", {
                errorCategory: "MEDIA_ERROR",
                errorCode: "HTML_AUDIO_ERROR",
              })}
              onTimeUpdate={(event) => setProgress(event.currentTarget.currentTime)}
              onLoadedMetadata={(event) => setDuration(Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : 0)}
              onEnded={() => {
                void reportPlaybackEvent("ended");
                if (playbackMode !== "one") move(1);
              }} />}


          <div className="progress">
            <input type="range" min="0" max={duration || 1} step="1" value={Math.min(progress, duration || 1)}
              disabled={!playable || !duration} aria-label="播放進度"
              aria-valuetext={formatTime(progress) + "，共 " + formatTime(duration)}
              style={{ "--played": (duration ? Math.min(100, progress / duration * 100) : 0) + "%" }}
              onChange={(event) => {
                const value = Number(event.target.value);
                setProgress(value);
                if (audioRef.current) audioRef.current.currentTime = value;
              }}/>
            <div className="time-labels"><span>{formatTime(progress)}</span><span>{formatTime(duration)}</span></div>
          </div>
          <div className="transport">
            <button type="button" className={"icon-button playback-mode " + (playbackMode !== "list" ? "active" : "")}
              onClick={cyclePlaybackMode} aria-label={`${playbackModeMeta.label}；切換播放模式`}
              title={playbackModeMeta.label}><Icon name={playbackModeMeta.icon} size={22}/></button>
            <button type="button" className="icon-button" onClick={() => move(-1)} disabled={!hasSequence || busy} aria-label="上一首"><Icon name="previous" size={26}/></button>
            <button type="button" className="play-button" onClick={togglePlayback} disabled={!playable} aria-label={playing ? "暫停" : "播放"}>
              {busy ? <span className="spinner"/> : <Icon name={playing ? "pause" : "play"} size={34}/>}
            </button>
            <button type="button" className="icon-button" onClick={() => move(1)} disabled={!hasSequence || busy} aria-label="下一首"><Icon name="next" size={26}/></button>
            <button type="button" className="icon-button" onClick={() => openLibrary("queue")} aria-label="開啟播放列表"><Icon name="list" size={22}/></button>
          </div>
          <div className="player-actions">
            <button type="button" className={"text-button " + (favorite ? "active" : "")} disabled={!currentSong}
              onClick={() => currentSong && toggleFavorite(currentSong)} aria-pressed={favorite}>
              <Icon name="heart" size={20} filled={favorite}/>{favorite ? "已收藏" : "收藏"}
            </button>
            <button type="button" className={"text-button " + (showLyrics ? "active" : "")} disabled={!currentSong}
              onClick={() => setShowLyrics((value) => !value)} aria-pressed={showLyrics}>
              <Icon name="lyrics" size={20}/>{showLyrics ? "顯示封面" : "顯示歌詞"}
            </button>
          </div>
          {showLyrics && <LyricsPicker key={currentSong?.recordingId}
            candidates={lyricsCandidates} selectedId={selectedLyricsId} onSelect={selectLyrics}
            query={lyricsQuery} onQueryChange={setLyricsQuery} onSearch={searchLyrics} searching={searchingLyrics}/>}
          {candidate?.playbackMode === "official_embed" && <p className="status">請使用上方官方播放器控制播放。</p>}
        </section>
      </main>

      {currentSong && <section className="mini-player" aria-label="迷你播放器">
        <progress max={duration || 1} value={Math.min(progress, duration || 1)} aria-label="目前歌曲播放進度"/>
        <button type="button" ref={miniPlayerRef} className="mini-song" onClick={() => setPlayerOpen(true)} aria-label="展開播放器">
          <Artwork song={currentSong} className="mini-art"/>
          <span><strong>{currentSong.title || "未命名歌曲"}</strong>
            <small>{busy ? "正在準備播放…" : candidate?.playbackMode === "official_embed" ? "開啟官方播放器" : (currentSong.artists || []).join("、") || "未知演出者"}</small></span>
        </button>
        <button type="button" className="icon-button mini-play" disabled={!playable} onClick={togglePlayback} aria-label={playing ? "暫停" : "播放"}>
          {busy ? <span className="spinner"/> : <Icon name={playing ? "pause" : "play"} size={24}/>}
        </button>
        <button type="button" className="icon-button mini-next" onClick={() => move(1)} disabled={!hasSequence || busy} aria-label="下一首"><Icon name="next" size={22}/></button>
        <button type="button" className="icon-button" onClick={() => openLibrary("queue")} aria-label="開啟播放列表"><Icon name="list" size={22}/></button>
      </section>}
      <nav className="bottom-nav" aria-label="主要導覽">{navigationButtons}</nav>
      {(pendingUndo || status) && <div className={"toast " + (pendingUndo ? "has-action" : "")} role="status" aria-live="polite">
        <span>{pendingUndo?.message || status}</span>
        {pendingUndo && <button type="button" className="toast-action" onClick={restoreRemovedSong}>復原</button>}
        <button type="button" className="toast-close" aria-label="關閉提示" onClick={() => { setPendingUndo(null); setStatus(""); }}>
          <Icon name="close" size={18}/>
        </button>
      </div>}

      <AddDestinationSheet song={addTargetSong} playlists={playlists}
        favorite={addTargetSong ? favoritesIds.has(addTargetSong.recordingId) : false}
        onClose={() => setAddTargetSong(null)} onAddQueue={addToQueue} onAddFavorite={addToFavorites}
        onAddPlaylist={addToPlaylist} onCreatePlaylist={createPlaylistWithSong}/>
      {playlistEditor && <Modal label={playlistEditor.mode === "rename" ? "重新命名歌單" : "新增歌單"} onClose={() => setPlaylistEditor(null)}>
        <form className="playlist-editor" onSubmit={savePlaylist}>
          <div className="destination-head"><h2>{playlistEditor.mode === "rename" ? "重新命名歌單" : "新增歌單"}</h2>
            <button type="button" className="icon-button" onClick={() => setPlaylistEditor(null)} aria-label="關閉"><Icon name="close"/></button>
          </div>
          <label htmlFor="playlist-name">歌單名稱</label>
          <input id="playlist-name" value={playlistEditor.name} autoFocus required maxLength={80} placeholder="例如：通勤路上"
            onChange={(event) => setPlaylistEditor((editor) => ({ ...editor, name: event.target.value }))}/>
          <div className="editor-actions">
            <button type="button" className="text-button" onClick={() => setPlaylistEditor(null)}>取消</button>
            <button type="submit" className="primary-button" disabled={!text(playlistEditor.name)}>儲存</button>
          </div>
        </form>
      </Modal>}
      {deletePlaylistTarget && <Modal label="刪除歌單" onClose={() => setDeletePlaylistTarget(null)}>
        <section className="confirm-dialog">
          <div className="destination-head"><div><span>刪除歌單</span><h2>{deletePlaylistTarget.name}</h2></div>
            <button type="button" className="icon-button" onClick={() => setDeletePlaylistTarget(null)} aria-label="關閉"><Icon name="close"/></button>
          </div>
          <p>歌單內歌曲不會從「我的收藏」或「播放列表」移除。</p>
          <div className="editor-actions">
            <button type="button" className="text-button" onClick={() => setDeletePlaylistTarget(null)}>取消</button>
            <button type="button" className="primary-button danger-button" onClick={confirmDeletePlaylist}>刪除歌單</button>
          </div>
        </section>
      </Modal>}
      {settingsOpen && <SettingsSheet health={apiHealth} install={pwaInstall}
        selectedSources={selectedSources} sourceOptions={SOURCE_OPTIONS}
        onClose={() => setSettingsOpen(false)} onInstall={() => void installPwa()}
        onSelectSource={selectApiSource}/>}
    </div>
  );
}
